import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { BreadcrumbJsonLd } from '../components/BreadcrumbJsonLd';
import { RelatedTools } from '../components/RelatedTools';
import {
  ArrowRight,
  CloudSun,
  Drop,
  Gauge,
  Thermometer,
  Wind,
  Sun,
  Leaf,
  ShieldCheck,
  Warning,
  CircleNotch,
  MapPin,
  Clock,
  Waves,
} from '@phosphor-icons/react';

/**
 * BYOS — Bring Your Own Station.
 *
 * Public page that lets anyone enter a Wunderground PWS station ID and get
 * back hyper-local disease pressure + spray-window intelligence. Positioned
 * as the enthusiast entry point for the TurfPulse data brain without needing
 * to sign up. Demonstrates the product's core value prop: we turn your
 * backyard weather data into spray decisions.
 */

const DEMO_STATIONS = [
  { id: 'KTXGEORG823', label: 'Teravista, Georgetown TX', note: "Kenny's station" },
  { id: 'KTXROUND582', label: 'Paloma Lake, Round Rock TX', note: 'Neighborhood reference' },
];

const RISK_STYLES = {
  low: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10',
  moderate: 'text-amber-400 border-amber-500/30 bg-amber-500/10',
  elevated: 'text-rose-400 border-rose-500/30 bg-rose-500/10',
  high: 'text-rose-400 border-rose-500/30 bg-rose-500/10',
};

const riskPillClass = (level) =>
  `inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-medium uppercase tracking-wider ${
    RISK_STYLES[level] || 'text-zinc-400 border-zinc-600/40 bg-zinc-600/10'
  }`;

const Stat = ({ icon: Icon, label, value, unit, mono = true }) => (
  <div className="flex flex-col gap-1 p-4 bg-zinc-900/60 border border-zinc-800 rounded-lg">
    <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-wider">
      {Icon && <Icon size={14} weight="regular" />}
      <span>{label}</span>
    </div>
    <div className={`text-2xl font-semibold text-zinc-100 ${mono ? 'font-mono' : ''}`}>
      {value ?? <span className="text-zinc-600">—</span>}
      {value !== null && value !== undefined && unit && (
        <span className="ml-1 text-sm font-normal text-zinc-500">{unit}</span>
      )}
    </div>
  </div>
);

const LocalTime = ({ iso }) => {
  if (!iso) return <span className="text-zinc-600">—</span>;
  try {
    const d = new Date(iso);
    return (
      <span className="font-mono text-zinc-300">
        {d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
        <span className="text-zinc-600 ml-1 text-xs">
          {d.toLocaleDateString([], { month: 'short', day: 'numeric' })}
        </span>
      </span>
    );
  } catch {
    return <span className="text-zinc-500">{iso}</span>;
  }
};

const StationResult = ({ data }) => {
  if (!data) return null;
  if (data.status === 'no_data') {
    return (
      <div
        data-testid="byos-nodata"
        className="mt-8 p-6 border border-amber-500/30 bg-amber-500/5 rounded-lg"
      >
        <div className="flex items-start gap-3">
          <Warning size={20} className="text-amber-400 mt-0.5" />
          <div>
            <div className="text-amber-300 font-medium text-sm">
              Station <span className="font-mono">{data.station_id}</span> isn't reporting data.
            </div>
            <div className="text-zinc-400 text-sm mt-1 leading-relaxed">
              It may be registered but not yet online, or offline for maintenance. Verify the
              station ID on{' '}
              <a
                href={`https://www.wunderground.com/dashboard/pws/${data.station_id}`}
                target="_blank"
                rel="noreferrer"
                className="text-amber-300 underline hover:text-amber-200"
              >
                wunderground.com
              </a>
              .
            </div>
          </div>
        </div>
      </div>
    );
  }

  const a = data.analytics || {};
  const sprayOpen = a.spray_window_open === true;

  return (
    <div data-testid="byos-result" className="mt-8 space-y-6">
      {/* Station header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 pb-4 border-b border-zinc-800">
        <div>
          <div className="text-zinc-500 text-xs uppercase tracking-[0.2em] mb-1">Station</div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <div className="text-2xl md:text-3xl font-semibold text-zinc-100 font-mono">
              {data.station_id}
            </div>
            {data.neighborhood && (
              <div className="flex items-center gap-1 text-zinc-400 text-sm">
                <MapPin size={14} />
                <span>
                  {data.neighborhood}
                  {data.country ? `, ${data.country}` : ''}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 text-zinc-500 text-sm">
          <Clock size={14} />
          <span>Observed</span>
          <LocalTime iso={data.observation_time_utc} />
        </div>
      </div>

      {/* Spray window headline — the one-glance answer */}
      <div
        data-testid="byos-spray-headline"
        className={`flex items-center gap-4 p-5 rounded-lg border ${
          sprayOpen
            ? 'border-emerald-500/40 bg-emerald-500/5'
            : 'border-zinc-700 bg-zinc-900/60'
        }`}
      >
        <div
          className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
            sprayOpen ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-800 text-zinc-500'
          }`}
        >
          {sprayOpen ? <ShieldCheck size={28} weight="fill" /> : <Warning size={26} />}
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-0.5">
            Spray Window
          </div>
          <div
            className={`text-xl font-semibold ${
              sprayOpen ? 'text-emerald-300' : 'text-zinc-200'
            }`}
          >
            {sprayOpen ? 'Open — conditions favorable' : 'Closed — wait for better conditions'}
          </div>
          <div className="text-zinc-500 text-sm mt-1">
            Based on dew-point spread ≥6°F and wind &lt;10 mph.
          </div>
        </div>
      </div>

      {/* Core stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Thermometer} label="Air Temp" value={data.temperature_f} unit="°F" />
        <Stat icon={Drop} label="Dew Point" value={data.dewpoint_f} unit="°F" />
        <Stat icon={Waves} label="Humidity" value={data.humidity_pct} unit="%" />
        <Stat
          icon={Wind}
          label="Wind"
          value={
            data.wind_speed_mph !== null && data.wind_speed_mph !== undefined
              ? `${data.wind_speed_mph}`
              : null
          }
          unit="mph"
        />
        <Stat
          icon={Drop}
          label="Rain (24h)"
          value={data.precip_total_in}
          unit="in"
        />
        <Stat icon={Gauge} label="Pressure" value={data.pressure_in} unit="in" />
        <Stat icon={Sun} label="Solar" value={data.solar_radiation_wm2} unit="W/m²" />
        <Stat icon={Sun} label="UV" value={data.uv_index} unit="" />
      </div>

      {/* Disease pressure block */}
      <div className="p-5 bg-zinc-950/60 border border-zinc-800 rounded-lg">
        <div className="flex items-center gap-2 mb-4">
          <Leaf size={18} className="text-emerald-500" />
          <h3 className="text-zinc-200 text-sm uppercase tracking-[0.2em]">
            Disease Pressure
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-zinc-500 uppercase tracking-wider">
              Dew Point Spread
            </span>
            <span className="text-xl font-mono text-zinc-100">
              {a.dewpoint_spread_f ?? '—'}
              {a.dewpoint_spread_f != null && (
                <span className="text-sm text-zinc-500 ml-1">°F</span>
              )}
            </span>
            {a.leaf_wetness_risk && (
              <span className={riskPillClass(a.leaf_wetness_risk)}>
                leaf wetness · {a.leaf_wetness_risk}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Dollar Spot</span>
            <span className="text-xl font-mono text-zinc-100">
              {a.dollar_spot_point_probability != null
                ? `${Math.round(a.dollar_spot_point_probability * 100)}%`
                : '—'}
            </span>
            <span className="text-xs text-zinc-500">Smith-Kerns point proxy</span>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Brown Patch</span>
            {a.brown_patch_risk ? (
              <span className={riskPillClass(a.brown_patch_risk)}>{a.brown_patch_risk}</span>
            ) : (
              <span className="text-zinc-600">—</span>
            )}
            <span className="text-xs text-zinc-500">Rhizoctonia solani</span>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Pythium</span>
            {a.pythium_risk ? (
              <span className={riskPillClass(a.pythium_risk)}>{a.pythium_risk}</span>
            ) : (
              <span className="text-zinc-600">—</span>
            )}
            <span className="text-xs text-zinc-500">Pythium blight</span>
          </div>
        </div>
      </div>

      {/* Footnote */}
      <div className="text-xs text-zinc-600 leading-relaxed">
        Data from Weather Underground Personal Weather Station network. Single-point
        probabilities are useful for alerting, not for spray decisions — use TurfPulse's full
        time-series disease models for treatment timing. Station readings reflect backyard
        conditions at the time of observation and can diverge from airport-grid forecasts by
        5–10°F on dew point.
      </div>
    </div>
  );
};

const BYOSPage = () => {
  const [stationId, setStationId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchStation = useCallback(async (id) => {
    const clean = (id || '').trim().toUpperCase();
    if (!clean) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const res = await axios.get(`${API_URL}/api/pws/disease/${encodeURIComponent(clean)}`);
      setData(res.data);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          'Could not reach the station. Check the ID and try again.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    fetchStation(stationId);
  };

  const handleDemoClick = (id) => {
    setStationId(id);
    fetchStation(id);
  };

  useEffect(() => {
    document.title = 'BYOS — Bring Your Own Weather Station | TurfPulse';
  }, []);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <SEO
        title="BYOS — Bring Your Own Weather Station"
        description="Got a Wunderground PWS or Ecowitt? Plug in your station ID and your dashboard runs on your backyard's air temp, humidity, and rainfall — not the airport 8 miles away."
        keywords="Wunderground PWS, personal weather station, Ecowitt lawn, hyperlocal weather, backyard weather station, lawn care weather"
        path="/byos"
      />
      <BreadcrumbJsonLd items={[
        { label: 'Home', path: '/' },
        { label: 'BYOS', path: '/byos' },
      ]} />
      {/* Header / hero */}
      <section className="border-b border-zinc-800/80 bg-gradient-to-b from-zinc-900 to-zinc-950">
        <div className="max-w-5xl mx-auto px-5 md:px-8 pt-16 pb-10">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-emerald-500 mb-4">
            <CloudSun size={14} weight="fill" />
            <span>BYOS · Personal Weather Station</span>
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-zinc-50"
            data-testid="byos-title"
          >
            Your backyard data.
            <br />
            <span className="text-emerald-500">Your disease model.</span>
          </h1>
          <p className="mt-5 text-zinc-400 text-base max-w-2xl leading-relaxed">
            Wunderground's gridded forecast is anchored to an airport 5–10 miles away.
            Your Personal Weather Station sits on your fence. For disease pressure and
            spray windows, that difference is the entire game. Paste a station ID below —
            we'll pull it live and run it through our models.
          </p>

          <form
            onSubmit={onSubmit}
            className="mt-8 flex flex-col sm:flex-row gap-2 max-w-xl"
            data-testid="byos-form"
          >
            <input
              data-testid="byos-station-input"
              value={stationId}
              onChange={(e) => setStationId(e.target.value)}
              placeholder="e.g. KTXGEORG823"
              className="flex-1 px-4 py-3 bg-zinc-900 border border-zinc-700 focus:border-emerald-500 outline-none rounded-lg text-zinc-100 font-mono placeholder-zinc-600 transition-colors"
              autoCapitalize="characters"
              spellCheck="false"
              maxLength={24}
            />
            <button
              type="submit"
              disabled={loading || !stationId.trim()}
              data-testid="byos-fetch-btn"
              className="flex items-center justify-center gap-2 px-5 py-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
            >
              {loading ? (
                <>
                  <CircleNotch size={18} className="animate-spin" />
                  Fetching…
                </>
              ) : (
                <>
                  Run Disease Model
                  <ArrowRight size={18} weight="bold" />
                </>
              )}
            </button>
          </form>

          {/* Demo chips */}
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-xs uppercase tracking-wider text-zinc-600 mr-1 self-center">
              Try a demo:
            </span>
            {DEMO_STATIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => handleDemoClick(s.id)}
                data-testid={`byos-demo-${s.id}`}
                className="group px-3 py-1.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-emerald-500/50 rounded-md text-xs text-zinc-400 hover:text-zinc-100 transition-all"
              >
                <span className="font-mono text-emerald-400/90 group-hover:text-emerald-400">
                  {s.id}
                </span>
                <span className="ml-2 text-zinc-500 group-hover:text-zinc-400">
                  {s.label}
                </span>
              </button>
            ))}
          </div>

          {error && (
            <div
              data-testid="byos-error"
              className="mt-6 p-4 border border-rose-500/30 bg-rose-500/5 rounded-lg text-rose-300 text-sm"
            >
              {error}
            </div>
          )}

          <StationResult data={data} />
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 py-16 border-b border-zinc-800/80">
        <h2 className="text-zinc-300 text-sm uppercase tracking-[0.2em] mb-8">
          Why this matters
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <div className="text-emerald-500 text-sm font-semibold mb-2">
              Gridded forecast ≠ your lawn
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed">
              Open-Meteo, NWS, and most consumer apps use a weather-model cell anchored to
              the nearest airport. Your lawn's dew point can vary 5°F from that station in
              the same neighborhood.
            </p>
          </div>
          <div>
            <div className="text-emerald-500 text-sm font-semibold mb-2">
              Dew point is everything
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed">
              The Smith-Kerns dollar spot model is a regression on temperature and relative
              humidity. Both come from PWS accuracy. Feed it airport data and you're
              guessing; feed it your fence and you're measuring.
            </p>
          </div>
          <div>
            <div className="text-emerald-500 text-sm font-semibold mb-2">
              Data you already own
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed">
              If you run an Ecowitt, Ambient, Davis, or any WU-compatible station, BYOS
              is free — we read the data you're already broadcasting publicly. No extra
              hardware required.
            </p>
          </div>
        </div>
      </section>

      {/* CTA footer */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 py-12">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="text-zinc-500 text-xs uppercase tracking-[0.2em] mb-2">
              Don't have a station?
            </div>
            <div className="text-zinc-200 text-lg">
              The TurfPulse Lab Starter Kit pairs the Ecowitt WS90 with a disease-model
              dashboard.
            </div>
          </div>
          <Link
            to="/kit"
            data-testid="byos-kit-cta"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-zinc-100 hover:bg-white text-zinc-900 font-medium rounded-lg transition-colors"
          >
            See the kit
            <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>
      <RelatedTools currentPath="/byos" />
    </div>
  );
};

export default BYOSPage;
